import axios from "./axios";

export interface XpRules {
  expPerRead: number;
  expPerComment: number;
  expPerPurchase: number;
  dailyCommentLimit: number;
}

export interface AchievementItem {
  id: string;
  title: string;
  description: string;
  iconUrl?: string;
  conditionType: string;
  conditionValue: number;
  nameColor?: string;
  avatarFrame?: string;
}

export async function getXpRules(): Promise<XpRules> {
  try {
    const res = await axios.get("/admin/gamification/xp-rules");
    const data = res.data ?? {};
    return {
      expPerRead: Number(data.expPerRead ?? data.ExpPerRead ?? 1),
      expPerComment: Number(data.expPerComment ?? data.ExpPerComment ?? 2),
      expPerPurchase: Number(data.expPerPurchase ?? data.ExpPerPurchase ?? 5),
      dailyCommentLimit: Number(data.dailyCommentLimit ?? data.DailyCommentLimit ?? 3),
    };
  } catch (err) {
    console.error("getXpRules error", err);
    return { expPerRead: 1, expPerComment: 2, expPerPurchase: 5, dailyCommentLimit: 3 };
  }
}

export async function getAchievements(): Promise<AchievementItem[]> {
  try {
    const res = await axios.get("/admin/gamification/achievements");
    if (!Array.isArray(res.data)) {
      return [];
    }

    return (res.data as any[]).map((item) => ({
      id: String(item.id ?? item.Id ?? ""),
      title: item.title ?? item.Title ?? "",
      description: item.description ?? item.Description ?? "",
      iconUrl: item.iconUrl ?? item.IconUrl ?? undefined,
      conditionType: item.conditionType ?? item.ConditionType ?? "read_count",
      conditionValue: Number(item.conditionValue ?? item.ConditionValue ?? 0),
      nameColor: item.nameColor ?? item.NameColor ?? undefined,
      avatarFrame: item.avatarFrame ?? item.AvatarFrame ?? undefined,
    }));
  } catch (err) {
    console.error("getAchievements error", err);
    return [];
  }
}

export async function saveXpRules(rules: XpRules): Promise<{ success: boolean }> {
  try {
    const res = await axios.post("/admin/gamification/xp-rules", rules);
    return { success: Boolean(res.data?.success ?? true) };
  } catch (err) {
    console.error("saveXpRules error", err);
    return { success: false };
  }
}

export async function updateAchievement(ach: AchievementItem): Promise<{ success: boolean }> {
  try {
    const payload = {
      id: ach.id,
      title: ach.title,
      description: ach.description,
      iconUrl: ach.iconUrl,
      conditionType: ach.conditionType,
      conditionValue: ach.conditionValue,
      nameColor: ach.nameColor?.trim() || null,
      avatarFrame: ach.avatarFrame?.trim() || null,
    };
    const res = await axios.post("/admin/gamification/achievements/update", payload);
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("updateAchievement error", err);
    return { success: false };
  }
}

export async function createAchievement(ach: Omit<AchievementItem, "id">): Promise<{ success: boolean; id?: string }>
{
  try {
    const payload = {
      title: ach.title,
      description: ach.description,
      iconUrl: ach.iconUrl,
      conditionType: ach.conditionType,
      conditionValue: ach.conditionValue,
      nameColor: ach.nameColor?.trim() || null,
      avatarFrame: ach.avatarFrame?.trim() || null,
    };
    const res = await axios.post("/admin/gamification/achievements/create", payload);
    return { success: Boolean(res.data?.success), id: res.data?.id };
  } catch (err) {
    console.error("createAchievement error", err);
    return { success: false };
  }
}

export async function deleteAchievement(id: string): Promise<{ success: boolean }>
{
  try {
    const res = await axios.post("/admin/gamification/achievements/delete", { id });
    return { success: Boolean(res.data?.success) };
  } catch (err) {
    console.error("deleteAchievement error", err);
    return { success: false };
  }
}

export async function bootstrapDefaultAchievements(overwriteExisting = false): Promise<{
  success: boolean;
  created: number;
  updated: number;
  awarded: number;
}> {
  try {
    const res = await axios.post("/admin/gamification/achievements/bootstrap-defaults", {
      overwriteExisting,
    });

    return {
      success: Boolean(res.data?.success),
      created: Number(res.data?.created ?? 0),
      updated: Number(res.data?.updated ?? 0),
      awarded: Number(res.data?.awarded ?? 0),
    };
  } catch (err) {
    console.error("bootstrapDefaultAchievements error", err);
    return { success: false, created: 0, updated: 0, awarded: 0 };
  }
}
